<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import {
  getEventStatusBadge,
  formatFollowUpTime,
} from 'dashboard/components-next/FollowUp/composables/useFollowUp.js';

const props = defineProps({
  event: { type: Object, required: true },
  compact: { type: Boolean, default: false },
});

// Reschedule/Complete need a dialog and a refetch that only the Calendar
// page can own — this chip just reports what the agent picked.
const emit = defineEmits(['reschedule', 'complete']);

const { t } = useI18n();
const router = useRouter();

// Same color tokens Label.vue already exposes — dot color always matches
// whatever badge color getEventStatusBadge would render for this event, so
// this can never drift from the Agenda view's badge.
const COLOR_CLASSES = {
  iris: 'bg-n-iris-9',
  ruby: 'bg-n-ruby-9',
  amber: 'bg-n-amber-9',
  slate: 'bg-n-slate-9',
};

const statusBadge = computed(() =>
  getEventStatusBadge(props.event.date, props.event.isMeeting)
);
const dotClass = computed(
  () => COLOR_CLASSES[statusBadge.value?.color || 'slate']
);
const time = computed(() => formatFollowUpTime(props.event.date));
const meetingUrl = computed(
  () => props.event.contact.custom_attributes?.meeting_url
);
// A physical meeting has no join link - it gets a location pin instead of the video icon.
const isPhysicalMeeting = computed(
  () =>
    props.event.contact.custom_attributes?.follow_up_type === 'physical_meeting'
);

const openContact = () => {
  router.push({
    name: 'contacts_edit',
    params: { contactId: props.event.contact.id },
  });
};

const showMenu = ref(false);
const menuItems = computed(() => [
  ...(meetingUrl.value
    ? [
        {
          label: t('CALENDAR.EVENT_MENU.JOIN'),
          action: 'join',
          value: 'join',
          icon: 'i-lucide-video',
        },
      ]
    : []),
  {
    label: t('CALENDAR.EVENT_MENU.RESCHEDULE'),
    action: 'reschedule',
    value: 'reschedule',
    icon: 'i-lucide-calendar-clock',
  },
  {
    label: t('CALENDAR.EVENT_MENU.OPEN_CONTACT'),
    action: 'open_contact',
    value: 'open_contact',
    icon: 'i-lucide-user',
  },
  {
    label: t('CALENDAR.EVENT_MENU.COMPLETE'),
    action: 'complete',
    value: 'complete',
    icon: 'i-lucide-check',
  },
]);

const handleMenuAction = ({ action }) => {
  showMenu.value = false;
  if (action === 'join') window.open(meetingUrl.value, '_blank', 'noopener');
  else if (action === 'reschedule') emit('reschedule', props.event);
  else if (action === 'open_contact') openContact();
  else if (action === 'complete') emit('complete', props.event);
};
</script>

<template>
  <div
    class="relative flex items-center w-full gap-1.5 px-1.5 py-1 rounded hover:bg-n-alpha-2"
  >
    <button
      type="button"
      class="flex items-center flex-1 min-w-0 gap-1.5 text-left"
      @click.stop="openContact"
    >
      <span class="flex-shrink-0 rounded-full size-1.5" :class="dotClass" />
      <span
        v-if="!compact"
        class="text-[11px] font-medium text-n-slate-10 tabular-nums flex-shrink-0"
      >
        {{ time }}
      </span>
      <span class="text-xs truncate text-n-slate-12">
        {{ event.contact.name }}
      </span>
    </button>
    <button
      v-if="event.isMeeting"
      v-on-clickaway="() => (showMenu = false)"
      type="button"
      class="flex-shrink-0"
      @click.stop="showMenu = !showMenu"
    >
      <Icon
        :icon="isPhysicalMeeting ? 'i-lucide-map-pin' : 'i-lucide-video'"
        class="size-3 text-n-iris-10"
      />
    </button>
    <DropdownMenu
      v-if="showMenu"
      :menu-items="menuItems"
      class="z-10 mt-1 w-40 ltr:right-0 rtl:left-0 top-full"
      @action="handleMenuAction"
    />
  </div>
</template>
